import { isNftToken } from './brand.helpers'
import {
  exchangeProviderTitleForTransaction,
  formatCryptoValue,
  isCryptoBrand,
  isExchangeEventType
} from './pointsExchange.helpers'
import customConfig from 'customConfig'
import {
  LOYALTY_EVENT_TYPES,
  TRANSACTION_STATUS,
  TRANSACTION_STATUS_LABEL,
  TRANSACTION_TYPE,
  TRIGGERED_BY_LABEL_MATCH
} from '../constants/transactions'
import { isExistsElementFromList } from './app/app.helpers'
import { formatTicks } from '../util/string.helpers'
import {
  checkTriggeredByLabel,
  isValidCashbackBonus
} from './translation.helpers'
import { triggeredByGameRandomValue } from './game.helpers'

const formatTransaction = (transaction, t, { symbol }, authId) => {
  const {
    insertedAt,
    loyaltyEventType,
    brand,
    token,
    tokenTo,
    amountReceived,
    pointsBurned,
    user,
    showBrandName,
    effectiveAt,
    txHash,
    triggeredBy
  } = transaction
  let status = transaction.status
  let title = buildTransactionTitle(transaction, t, symbol)
  const isTriggeredByGameRandomValue = checkTriggeredByLabel(
    triggeredBy,
    triggeredByGameRandomValue()
  )
  let heading = isTriggeredByGameRandomValue
    ? t('game.prize-won-from-scratch-card')
    : t(`${customConfig.eventTypeLabel}.${loyaltyEventType}`)
  let type = TRANSACTION_TYPE.REWARD
  let label = formatTicks(amountReceived)
  const isNft = isNftToken(token)
  if (
    isExistsElementFromList(
      [LOYALTY_EVENT_TYPES.POINTS_TO_REWARDS, LOYALTY_EVENT_TYPES.DEBIT_POINTS],
      loyaltyEventType
    )
  ) {
    label = formatTicks(pointsBurned)
    type = TRANSACTION_TYPE.REDEEM
  } else if (isExchangeEventType(loyaltyEventType)) {
    if (
      showBrandName &&
      token?.id &&
      tokenTo?.id &&
      token?.brandId === tokenTo?.brandId
    ) {
      heading = isTriggeredByGameRandomValue
        ? t('game.opened-scratch-card')
        : t(
            `${customConfig.eventTypeLabel}.${LOYALTY_EVENT_TYPES.POINTS_TO_REWARDS}`
          )
    } else if (isNft) {
      heading = t(customConfig.nftPurchaseLabel)
      title = tokenTo?.name
    }
    label = isCryptoBrand(brand?.id)
      ? formatTicks(formatCryptoValue(amountReceived || pointsBurned))
      : formatTicks(amountReceived || pointsBurned)
    if (loyaltyEventType === LOYALTY_EVENT_TYPES.REVERT_EXCHANGE) {
      type = amountReceived ? TRANSACTION_TYPE.REDEEM : TRANSACTION_TYPE.REWARD
    } else {
      type = amountReceived ? TRANSACTION_TYPE.REWARD : TRANSACTION_TYPE.REDEEM
    }
    if (status === TRANSACTION_STATUS.CANCELLED) {
      heading = t(transactionTitleLabel(isNft))
    } else if (
      effectiveAt &&
      status === TRANSACTION_STATUS.PENDING &&
      !txHash
    ) {
      status = TRANSACTION_STATUS_LABEL.FUTURE
      heading = t(transactionTitleLabel(isNft))
    }
  } else if (loyaltyEventType === LOYALTY_EVENT_TYPES.CODE_TO_NFT) {
    heading = t(customConfig.nftCreditLabel)
    title = token?.name
    label = 1
  } else if (loyaltyEventType === LOYALTY_EVENT_TYPES.NFT_PERSONALISATION) {
    heading = t('brand:nft.successful-engraving')
    title = token?.name
    label = 1
  } else if (loyaltyEventType === LOYALTY_EVENT_TYPES.CREDIT_NFT) {
    heading = t(customConfig.nftRewardLabel)
    title = token?.name
    label = 1
  } else if (loyaltyEventType === LOYALTY_EVENT_TYPES.NFT_TRANSFER) {
    heading = t(customConfig.nftTransferLabel)
    title = token?.name
    label = 1
    type =
      authId && authId === user?.authId
        ? TRANSACTION_TYPE.REDEEM
        : TRANSACTION_TYPE.REWARD
  } else if (loyaltyEventType === LOYALTY_EVENT_TYPES.NFT_SALE) {
    const isNftSeller = transaction?.isNftSeller
    if (isNftSeller) {
      const isSaleFee = transaction?.isSaleFee
      heading = t(customConfig.nftSaleLabel)
      if (showBrandName) {
        label = formatTicks(pointsBurned)
        title = token?.name
        type = TRANSACTION_TYPE.REWARD
      } else if (isSaleFee) {
        heading = t(customConfig.nftSaleFeeLabel)
        label = formatTicks(pointsBurned - amountReceived)
        title = token?.name
        type = TRANSACTION_TYPE.REDEEM
      } else {
        label = 1
        title = tokenTo?.name
        type = TRANSACTION_TYPE.REDEEM
      }
    } else {
      heading = t(customConfig.nftPurchaseLabel)
      label = showBrandName ? formatTicks(pointsBurned) : 1
      title = showBrandName ? token?.name : tokenTo?.name
      type = showBrandName ? TRANSACTION_TYPE.REDEEM : TRANSACTION_TYPE.REWARD
    }
  }

  if (
    [
      LOYALTY_EVENT_TYPES.CREDIT_NFT,
      LOYALTY_EVENT_TYPES.CODE_TO_NFT,
      LOYALTY_EVENT_TYPES.CREDIT_POINTS,
      LOYALTY_EVENT_TYPES.CODE_TO_POINTS
    ].includes(loyaltyEventType)
  ) {
    if (status === TRANSACTION_STATUS.CANCELLED) {
      heading = t(transactionTitleLabel(isNft))
    } else if (effectiveAt && !txHash) {
      status = TRANSACTION_STATUS_LABEL.FUTURE
      heading = t(transactionTitleLabel(isNft))
    }
  }

  // Handling cashback transactions title
  if (
    loyaltyEventType === LOYALTY_EVENT_TYPES.CREDIT_POINTS &&
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.CASHBACK)
  ) {
    heading = t(customConfig.cashbackFromLabel, {
      brand: triggeredBy
        .replace(new RegExp(TRIGGERED_BY_LABEL_MATCH.CASHBACK, 'i'), '')
        .trim()
    })
  } else if (
    checkTriggeredByLabel(
      triggeredBy,
      TRIGGERED_BY_LABEL_MATCH.TIER_CASHBACK_BONUS
    )
  ) {
    heading = t('membership.cashback-status-level-bonus-received')
  } else if (isValidCashbackBonus(triggeredBy)) {
    heading = t('membership.cashback-purchase-reward-received')
  } else if (
    checkTriggeredByLabel(
      triggeredBy,
      TRIGGERED_BY_LABEL_MATCH.MILESTONE_FEATURE
    )
  ) {
    heading = t('membership.staking-reward-claimed')
  } else if (
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.COE_INTEGRATION)
  ) {
    heading = t('membership.vip-store-item-purchased')
  } else if (
    checkTriggeredByLabel(
      triggeredBy,
      TRIGGERED_BY_LABEL_MATCH.LOYALTY_POINTS_PURCHASED
    )
  ) {
    heading = t('membership.points-purchased')
  } else if (
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.QUEST)
  ) {
    heading = t('quest.quest-completed')
  } else if (
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.REFERRAL)
  ) {
    heading = t('membership.successful-friend-referral')
  } else if (
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.SLEAP)
  ) {
    heading = t('membership.points-earned-by-sleap')
  } else if (checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.UGC)) {
    heading = t('membership.points-earned-from-post')
  } else if (
    checkTriggeredByLabel(triggeredBy, TRIGGERED_BY_LABEL_MATCH.PURCHASE_TIER)
  ) {
    heading = t('membership.purchase-status-upgrade', {
      tier: triggeredBy
        .replace(new RegExp(TRIGGERED_BY_LABEL_MATCH.PURCHASE_TIER, 'i'), '')
        .trim()
    })
  } else if (
    checkTriggeredByLabel(
      triggeredBy,
      TRIGGERED_BY_LABEL_MATCH.EMPLOYEE_FIRST_NAMES
    )
  ) {
    heading = t(
      `membership.${type === TRANSACTION_TYPE.REWARD ? 'rewarded' : 'debited'}-by-admin`
    )
  }

  return {
    date: t(customConfig.dateFormatLabel, { date: insertedAt }),
    status: status || TRANSACTION_STATUS.SUCCESS,
    title,
    heading,
    type,
    label,
    isNft
  }
}

const transactionTitleLabel = (isNft = false) => {
  return `reward.future-reward-${isNft ? 'nft' : 'points'}`
}

const transactionDescLabel = (isNft = false) => {
  return `reward.future-reward-desc-${isNft ? 'nft' : 'points'}`
}

export { formatTransaction, transactionTitleLabel, transactionDescLabel }

const buildTransactionTitle = (
  { token, brand, showBrandName, loyaltyEventType, symbol },
  t,
  tokenSymbol
) => {
  if (!showBrandName && brand?.name) {
    return exchangeProviderTitleForTransaction(t, brand, symbol)
  } else if (isExchangeEventType(loyaltyEventType)) {
    return token?.symbol
  }
  return tokenSymbol
}
